/**
 * MONSTER TOWN — THE HUD. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * Everything a kid can reach that is not the world itself. Hand-drawn cel chrome
 * from `palette.ts`, no emoji, no default browser look, no words (LAW #4).
 *
 * WHAT LIVES WHERE, AND WHY:
 *   top-left      coin purse — where money lives, so flying coins have a home
 *   under it      the rail: my monster · picture · shop / sound · which-kid.
 *                 A FIXED 3x2 BLOCK — the same five buttons in the same six
 *                 places in build and play, portrait and landscape, on the
 *                 hand-me-down and on the new iPad. A pre-reader navigates by
 *                 position; a control that moves between modes is, to them, a
 *                 different control (fixed r3).
 *   top-right     camera cluster, 2x2 so it fits portrait and landscape
 *   bottom-left   undo (build mode only)
 *   bottom-right  the ONE hammer that toggles build and play. Same corner, same
 *                 white tile, same hammer, in BOTH modes — build is a ring on
 *                 that button, never a different button (fixed r3).
 *   bottom-centre belongs to the BUILD CREW's piece drawer. Its `build.css`
 *                 insets itself by 104 px on both sides to clear these corner
 *                 buttons, so nothing here may exceed 96 px.
 *
 * THE NAMED CRITIC DEFECT — "camera that hides what you're placing" — is fixed
 * here: while the build ghost is under the camera cluster, the cluster fades and
 * shrinks toward the corner. It stays tappable (opacity, not display), so it can
 * never become a dead end.
 *
 * SAVE: this crew owns the `ui` section (nudge history). Registered inside
 * install(), which main.ts runs before `save.load()`.
 */

import './events'
import './ui.css'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import type { Phase, Vec3 } from '../core/types'
import { worldToCanvasPx } from '../input/pick'
import { app, type GameContext } from '../app/context'
import { selectedPlaceable, undo } from '../build/placer'
import type { VolumeMode } from '../audio/engine'
import { button, el, setIcon } from './dom'
import { iconEl } from './icons'
import { registerBuyPanel } from './buySheet'
import { installNudges } from './nudge'
import { installTutorial, type TutorialApi } from './tutorial'
import { PanelHost } from './panels'
import { registerPhotoPanel } from './photo'
import { registerShopPanel, shopLayout } from './shop'
import { thumbCacheSize } from './thumb3d'
import { registerSlotPanel } from './slots'
import { installSignposts } from './signposts'
import { installTickets } from './tickets'
import { installToasts } from './toast'
import { installTouchUx } from './touch'

interface UiSave {
  nudges: Record<string, number>
}

/** Speaker button pictures, one per volume state. DATA. */
const VOLUME_ICON: Record<VolumeMode, string> = {
  full: 'speakerFull',
  quiet: 'speakerQuiet',
  off: 'speakerOff',
}

/** How close the ghost gets before the camera buttons move out of its way. */
const DODGE_PAD_PX = 56
/** Seconds the cluster stays out of the way after the ghost last touched it. */
const DODGE_HOLD_S = 1.5

export function installHud(ctx: GameContext): void {
  const hud = ctx.hud
  hud.replaceChildren()

  // -- fade layer used by every scene transition ---------------------------
  const fade = el('div', '')
  fade.id = 'mt-fade'
  hud.append(fade)
  world.setFader(
    (dir, ms) =>
      new Promise<void>((resolve) => {
        fade.style.transitionDuration = `${ms}ms`
        fade.dataset['on'] = dir === 'out' ? 'true' : 'false'
        window.setTimeout(resolve, ms)
      }),
  )

  // -- coin purse -----------------------------------------------------------
  const purse = el('div', 'mt-purse')
  purse.append(iconEl('coin', 60))
  const purseNum = el('span', 'mt-num')
  purse.append(purseNum)
  hud.append(purse)

  // The purse counts UP rather than snapping. A number that ticks is a number a
  // kid watches; a number that jumps is one they miss entirely.
  let shownCoins = 0
  let bumpFor = 0
  const paintPurse = (): void => {
    purseNum.textContent = String(Math.round(shownCoins))
  }
  const snapCoins = (): void => {
    shownCoins = wallet.coins
    paintPurse()
  }
  bus.on('economy:earned', () => {
    bumpFor = 0.35
    purse.dataset['bump'] = '1'
  })
  bus.on('economy:spent', () => {
    bumpFor = 0.2
    purse.dataset['bump'] = '1'
  })
  bus.on('save:loaded', snapCoins)

  // -- camera cluster (top right, 2x2) -------------------------------------
  const cam = el('div', 'mt-cam')
  cam.dataset['dodge'] = '0'
  cam.append(
    button({ icon: 'rotateLeft', label: 'Turn the town left', cue: 'rotate' }, () => {
      ctx.camera.rotateLeft()
      bus.emit('ui:cameraRotate', { rot: ctx.camera.rotation })
    }),
    button({ icon: 'rotateRight', label: 'Turn the town right', cue: 'rotate' }, () => {
      ctx.camera.rotateRight()
      bus.emit('ui:cameraRotate', { rot: ctx.camera.rotation })
    }),
    button({ icon: 'zoomIn', label: 'Look closer', cue: 'zoom' }, () => {
      ctx.camera.zoomIn()
      bus.emit('ui:cameraZoom', { level: ctx.camera.zoomLevel })
    }),
    button({ icon: 'zoomOut', label: 'Look wider', cue: 'zoom' }, () => {
      ctx.camera.zoomOut()
      bus.emit('ui:cameraZoom', { level: ctx.camera.zoomLevel })
    }),
  )
  hud.append(cam)

  // -- the one hammer -------------------------------------------------------
  //
  // IT IS THE SAME BUTTON IN BOTH MODES, AND THAT IS THE WHOLE POINT (fixed r3).
  //
  // It used to SWAP ITS PICTURE: a white square with a hammer in play mode, an
  // orange square with a monster face in build mode. The reasoning ("show what
  // tapping it will give you") is adult reasoning. To a 3-year-old a button is
  // WHAT IT LOOKS LIKE — they learn "the hammer is how I build", go into build
  // mode, and the hammer is gone from the corner they left it in. There is no
  // way back that they can see, in the one mode that most needs an exit.
  //
  // The brief says "ONE clear toggle... same camera in both", and one toggle
  // means one identity: the same corner, the same white tile, the same hammer,
  // always. Build mode is shown as a STATE on that button — an orange ring and
  // a lifted look — never as a different button.
  //
  // ...AND THE STATE HAS TO BE READABLE WITHOUT READING (fixed after the owner
  // played it). The ring alone was a convention: it means "on" to somebody who
  // already knows there is an "on". The owner opened his own game and could not
  // tell which mode he was in, and a three-year-old has less to go on than he
  // does. The state is now carried FOUR ways at once, none of which is a word:
  //
  //   * the tile's COLOUR      white -> orange
  //   * a gold HALO            absent -> 6 px
  //   * the hammer itself      resting steel -> swinging, sparking, white-on-orange
  //   * a corner BADGE         a walking monster -> a brick
  //
  // The badge is the honest half: it says what tapping the WORLD does right now
  // ("you are walking" / "you are building"), which is the thing that was
  // actually unknowable. The hammer never changes identity or corner, so the r3
  // defect above stays fixed — this is state layered on top of it, not instead.
  const modeWrap = el('div', 'mt-row mt-botright')
  const modeBtn = button(
    { label: 'Build or play', className: 'big mt-mode', cue: null },
    () => app.toggleMode(),
  )
  const modeGlyph = el('span', 'mt-mode-glyph')
  const modeBadge = el('span', 'mt-mode-badge')
  modeBtn.append(modeGlyph, modeBadge)
  modeWrap.append(modeBtn)
  hud.append(modeWrap)

  // -- undo (build mode only) ----------------------------------------------
  //
  // IT BELONGS WITH THE PIECES, NOT IN A CORNER. Undo used to sit in its own
  // detached cream box in the bottom-left, a different size and a different
  // colour from every control it works on, reading as a stray fragment of the
  // drawer rather than part of it. It now docks into the build crew's
  // `#mt-build-dock` — in the drawer's always-visible row, so collapsing the
  // pieces can never take undo with them.
  //
  // The corner is kept as a FALLBACK and nothing depends on the dock existing:
  // if the drawer is not mounted (or the build crew ever removes the node) undo
  // is still there, in the place it has always been. Neither crew can break the
  // other by shipping first.
  const undoWrap = el('div', 'mt-row mt-botleft')
  const undoBtn = button({ icon: 'undo', label: 'Undo', className: 'big', cue: null }, () => {
    undo()
  })
  undoWrap.append(undoBtn)
  hud.append(undoWrap)

  let undoDocked = false
  /**
   * ============ UNDO WAS UNREACHABLE ON THE FIRST BUILD SCREEN (fixed r3) ============
   * Round-3 critic, blocker, measured on a real first launch: the undo button
   * rendered at canvas (16,708)-(112,804) — the CORNER fallback — but
   * `document.elementFromPoint` returned "Turn the piece" at all nine sample
   * points inside it. reachFrac 0.00. The drawer was sitting on top of it, so on
   * the exact screen where a 3-year-old starts tapping, the bin was one tap away
   * and the way back was dead. It only healed after a play/build toggle.
   *
   * TWO ROOT CAUSES, BOTH FIXED HERE:
   *
   *  1. The dock was only taken when it was already VISIBLE (`offsetParent`).
   *     On a first run the monster creator owns the screen, so at the moment the
   *     mode flipped to build the drawer's box was still zero — undo went to the
   *     corner and nothing ever asked again. The visibility test bought nothing:
   *     the dock lives in the drawer's tool row, which stays on screen when the
   *     pieces are collapsed, so "the dock exists" and "the dock is usable" are
   *     the same statement. Dock into it whenever it EXISTS; the corner is for
   *     the case where there is no drawer at all.
   *  2. Nothing re-docked when the drawer appeared for any reason other than a
   *     mode change. It now re-docks on the drawer's own `build:paletteMounted`
   *     (the build crew emits it every time it mounts) and on any phase change,
   *     which is what a creator closing actually is.
   * ==================================================================================
   */
  const dockUndo = (): void => {
    const dock = document.getElementById('mt-build-dock')
    undoDocked = dock !== null
    if (dock !== null) {
      if (!dock.contains(undoBtn)) dock.append(undoBtn)
    } else if (!undoWrap.contains(undoBtn)) {
      undoWrap.append(undoBtn)
    }
  }
  const syncUndo = (): void => {
    dockUndo()
    undoWrap.style.display = undoDocked ? 'none' : app.mode === 'build' ? 'flex' : 'none'
  }
  // The drawer mounts on a microtask queued during installBuild, which runs
  // BEFORE installHud — so a microtask queued here always runs after it.
  queueMicrotask(syncUndo)
  // Every way the drawer can come, go, or be rebuilt underneath us.
  bus.on('build:paletteToggled', syncUndo)
  bus.on('build:paletteMounted', syncUndo)
  bus.on('app:phaseChanged', syncUndo)
  bus.on('scene:entered', syncUndo)

  // -- panels ---------------------------------------------------------------
  const panels = new PanelHost(hud)
  const backToWorld = (): void => {
    panels.hide()
    const id = world.activeSceneId
    const kind = id === null ? 'exterior' : (world.get(id)?.def.kind ?? 'exterior')
    app.setPhase(kind === 'interior' ? 'houseInterior' : 'town')
  }
  registerPhotoPanel(ctx, panels, backToWorld)
  registerSlotPanel(panels, () => panels.hide())
  // "Do you want this?" — the shop's offer card. Nothing leaves the purse until
  // a kid taps YES on it.
  registerBuyPanel(panels)
  // The buy half of earn-and-buy. Was missing entirely: only 'photo' and
  // 'slots' were ever registered, so `openPanel('shop')` returned false and the
  // economy had no surface a kid could see.
  registerShopPanel(panels, () => panels.hide())

  /**
   * THE PHASE MACHINE AND THE PANEL HOST ARE THE SAME THING.
   *
   * `app.phase` is the contract's word for "what screen are we on", and three of
   * its values ARE panels. They were never connected: `app.setPhase('photo')`
   * flipped a string and nothing opened, so photo mode and the two-slot picker —
   * both brief requirements — were reachable only by tapping their buttons and
   * not at all through the API the harness, the tools and every other crew use.
   * That is why the captured "slot picker" and "photo mode" frames were just the
   * town with the HUD on it.
   *
   * Both directions are wired, or the two states drift apart: setting the phase
   * opens the panel, and closing the panel puts the phase back where the world
   * is. `monsterCreator` is deliberately absent — the monster crew owns that
   * sheet and already listens to `app:phaseChanged` itself.
   */
  const PHASE_PANEL: Partial<Record<Phase, string>> = {
    photo: 'photo',
    slotPick: 'slots',
    shop: 'shop',
  }

  const worldPhase = (): Phase => {
    const id = world.activeSceneId
    const kind = id === null ? 'exterior' : (world.get(id)?.def.kind ?? 'exterior')
    return kind === 'interior' ? 'houseInterior' : 'town'
  }

  bus.on('app:phaseChanged', (e) => {
    const want = PHASE_PANEL[e.phase]
    if (want !== undefined) {
      if (panels.open !== want) panels.show(want)
      return
    }
    // Left a panel phase for a world phase: the panel goes with it.
    if (PHASE_PANEL[e.previous] !== undefined && panels.open !== null) panels.hide()
  })

  // Any crew can open a panel with `{ kind: 'ui', panel: 'photo' }` feedback.
  bus.on('ui:panel', (e) => {
    // Photo mode shows the WORLD through a frame, so the chrome gets out of the
    // way entirely. Otherwise the kid lines up a shot around four buttons.
    hud.dataset['photo'] = e.panel === 'photo' ? '1' : '0'
    // A full-screen sheet is a card floating on the town now, not an opaque
    // cream fill, so the chrome underneath has to leave — see the
    // `#hud[data-panel='1']` block in ui.css.
    hud.dataset['panel'] = e.panel === null ? '0' : '1'
    if (e.panel === null) {
      if (panels.open !== null) panels.hide()
      // The other half of the wiring above: a kid tapping "back" out of the
      // photo frame must leave the `photo` phase too, or the phase is stuck on
      // a screen that is no longer there and setting it again does nothing.
      if (PHASE_PANEL[app.phase] !== undefined) app.setPhase(worldPhase())
      return
    }
    if (panels.open !== e.panel) panels.show(e.panel)
    for (const [phase, name] of Object.entries(PHASE_PANEL)) {
      if (name === e.panel && app.phase !== phase) app.setPhase(phase as Phase)
    }
  })

  // -- left stack: my monster, picture, sound, which-kid --------------------
  //
  // "MY MONSTER" IS NOT OPTIONAL CHROME. The creator used to be reachable on
  // first launch and never again: a kid who batted the green tick in the first
  // two seconds was locked to the default teal monster for the life of the save,
  // and hats earned later had no screen to be put on. This is the way back in,
  // and it is live in every scene and both modes.
  //
  // AND IT IS THE SAME FIVE BUTTONS, IN THE SAME PLACES, IN BOTH MODES (r3).
  //
  // The rail used to be a COLUMN that wrapped when the build drawer took the
  // bottom of the screen, so the same five controls were a 5-high column in
  // play mode and a 4+1 wrap in build mode — "which kid is playing" sat at the
  // BOTTOM of the column in one mode and at the TOP, beside "my monster", in
  // the other. Nothing about a control's meaning changes with the mode, so
  // nothing about where it sits may change either.
  //
  // It is now a fixed 3x2 block that fits above the drawer at every size and in
  // every orientation, so there is no layout left that can depend on the mode.
  const left = el('div', 'mt-leftstack mt-rail')
  const monsterBtn = button({ icon: 'myMonster', label: 'My monster' }, () =>
    app.setPhase('monsterCreator'),
  )
  const photoBtn = button({ icon: 'camera', label: 'Take a picture' }, () => panels.toggle('photo'))
  const soundBtn = button({ icon: 'speakerFull', label: 'Sound' }, () => {
    window.__audio?.cycleVolume()
  })
  // The shop lives on the HUD, not only behind a walk across the town. A kid
  // who has just earned coins can spend them from wherever they are standing.
  const shopBtn = button({ icon: 'bag', label: 'Shop' }, () => panels.toggle('shop'))
  // A HOUSE PICTURE ON THIS BUTTON WAS A MIS-PROMISE (fixed r2). To a
  // pre-reader a house is the picture of the place they live: they tapped it
  // expecting their own front door and got a system screen, and the other card
  // on that screen swaps their entire world for their sibling's empty town.
  // Nothing is erased and it is one tap back, but the icon was telling them
  // something the button does not do. Two monster faces say "which one of you
  // is playing", which is what it does. Their actual house is a building they
  // walk into — and now a signpost in their own monster's colour, so it is also
  // one tap from anywhere in the town (see `signposts.ts`).
  const kidBtn = button({ icon: 'who', label: 'Which town' }, () => panels.toggle('slots'))
  /**
   * SHOW ME AGAIN. The tutorial's own pointing paw, as a button.
   *
   * A three-year-old will want the first run again — it is the bit where a
   * friendly monster came over and played with them — and a parent handing the
   * iPad to the younger brother needs a way to bring it back that is not "erase
   * the town". The picture IS the tutorial: the paw on this button is the same
   * paw that lands on the ground when you press it, which is the only kind of
   * label a pre-reader can use (LAW #4).
   *
   * It fills the sixth slot of the 3x2 rail, so nothing else moved to make room
   * for it — the r3 rule that a control never changes position holds.
   */
  let tutorial: TutorialApi | null = null
  const helpBtn = button({ icon: 'paw', label: 'Show me again' }, () => {
    tutorial?.replay()
  })
  left.append(monsterBtn, photoBtn, shopBtn, soundBtn, kidBtn, helpBtn)
  hud.append(left)
  bus.on('audio:volumeChanged', (e) => setIcon(soundBtn, VOLUME_ICON[e.mode], 52))

  // -- layers ---------------------------------------------------------------
  installToasts(ctx, hud)
  const tickets = installTickets(hud)
  const touchUx = installTouchUx(ctx, hud)
  // Guidance that never runs out: labels on the doors, always there, never a
  // nag. See the long note at the top of signposts.ts.
  installSignposts(ctx, hud)
  const nudges = installNudges(hud, { selectedPlaceable: () => selectedPlaceable() })
  // The first run: a paw that points at what to tap next, gated on the kid
  // actually doing it, and silent. Installed after every other layer so it
  // paints on top of them (and above the build drawer — see its z-index).
  tutorial = installTutorial(ctx, hud, { selectedPlaceable: () => selectedPlaceable() })
  nudges.setAnchor('mode', modeBtn)
  nudges.setAnchor('undo', undoBtn)
  nudges.setAnchor('camera', cam)
  nudges.setAnchor('shop', shopBtn)
  nudges.setAnchor('slots', kidBtn)

  // -- how much room the build drawer is taking ----------------------------
  //
  // THE LEFT RAIL MUST NOT RUN INTO THE DRAWER. Five 76px buttons stacked from
  // under the purse reached ~562px down an 820px-tall iPad, and the build
  // drawer's top edge sits at roughly 500px — so the bottom rail button was
  // visually clipped by it. The rail is given the drawer's real height as a
  // custom property and stops above it, wrapping into a second column if it
  // must, so nothing is ever cut off and nothing is ever unreachable.
  //
  // Measured from the DRAWER'S OWN bounding rect. Never window.innerHeight: the
  // hard rule applies to layout as much as to hit-testing.
  const syncBuildHeight = (): void => {
    const drawer = document.getElementById('mt-build')
    const h = drawer === null || drawer.offsetParent === null ? 0 : drawer.getBoundingClientRect().height
    hud.style.setProperty('--build-h', `${Math.round(h)}px`)
  }
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => syncBuildHeight())
    queueMicrotask(() => {
      const drawer = document.getElementById('mt-build')
      if (drawer !== null) ro.observe(drawer)
      syncBuildHeight()
    })
  } else {
    queueMicrotask(syncBuildHeight)
  }
  bus.on('build:paletteToggled', syncBuildHeight)
  bus.on('scene:entered', syncBuildHeight)

  // -- mode-dependent chrome ------------------------------------------------
  const paintMode = (): void => {
    const building = app.mode === 'build'
    // The whole HUD knows which mode it is in, so chrome that has to make room
    // for the build drawer can do it in CSS instead of in five listeners.
    hud.dataset['mode'] = app.mode
    // STATE, NOT IDENTITY. It is the same hammer, in the same corner, in both
    // modes; what changes is colour, halo, whether the hammer is swinging, and
    // a corner badge showing what a tap on the WORLD does right now. See the
    // long note where the button is built.
    modeBtn.dataset['on'] = String(building)
    modeGlyph.replaceChildren(
      // White-on-orange in build mode: a gold hammer head on an orange tile is
      // the one combination that would not read at arm's length on an iPad.
      building ? iconEl('hammerOn', 60, { f: 'uiPanel' }) : iconEl('hammer', 60),
    )
    modeBadge.replaceChildren(iconEl(building ? 'block' : 'walker', 30))
    // The build crew re-mounts its drawer on this same event (its listener is
    // registered first, in installBuild), so re-dock before deciding whether the
    // corner fallback is needed. Docked, the drawer's own visibility already
    // hides undo outside build mode.
    syncUndo()
    if (!building) cam.dataset['dodge'] = '0'
    syncBuildHeight()
  }
  paintMode()
  bus.on('app:modeChanged', paintMode)

  // -- THE CAMERA-DODGE FIX -------------------------------------------------
  // Both rectangles are measured against the CANVAS bounding rect. Never window.
  //
  // The cluster's box in canvas space is CACHED. `build:ghostMoved` fires on
  // every drag sample, and calling getBoundingClientRect() twice per sample
  // forces a synchronous layout on the exact frames a kid is dragging a piece
  // around — the worst possible moment on the hand-me-down iPad. The box only
  // changes when the viewport does, so measure it then.
  let dodgeHold = 0
  let camBox: { l: number; t: number; r: number; b: number } | null = null
  const remeasure = (): void => {
    camBox = null
  }
  window.addEventListener('resize', remeasure)
  window.addEventListener('orientationchange', remeasure)
  bus.on('app:modeChanged', remeasure)
  bus.on('ui:panel', remeasure)

  bus.on('build:ghostMoved', (e) => {
    const sceneId = world.activeSceneId
    if (sceneId === null) return
    const node = world.get(sceneId)
    if (node === undefined) return
    const p: Vec3 = node.grid.cellToWorld(e.anchor)
    const screen = worldToCanvasPx(ctx.camera.camera, ctx.canvas, p)
    if (screen.behind) return
    if (camBox === null) {
      const canvasRect = ctx.canvas.getBoundingClientRect()
      const camRect = cam.getBoundingClientRect()
      camBox = {
        l: camRect.left - canvasRect.left - DODGE_PAD_PX,
        t: camRect.top - canvasRect.top - DODGE_PAD_PX,
        r: camRect.right - canvasRect.left + DODGE_PAD_PX,
        b: camRect.bottom - canvasRect.top + DODGE_PAD_PX,
      }
    }
    const inside =
      screen.x >= camBox.l && screen.x <= camBox.r && screen.y >= camBox.t && screen.y <= camBox.b
    if (inside) {
      cam.dataset['dodge'] = '1'
      dodgeHold = DODGE_HOLD_S
    }
  })

  // -- save section ---------------------------------------------------------
  registerSaveSection<UiSave>(
    {
      id: 'ui',
      version: 1,
      defaults: () => ({ nudges: {} }),
      serialize: () => ({ nudges: nudges.counts() }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const src = c.isRecord(raw['nudges']) ? raw['nudges'] : {}
        const out: Record<string, number> = {}
        for (const [k, v] of Object.entries(src)) out[k] = c.num(v, 0)
        return { nudges: out }
      },
    },
    (v) => {
      nudges.restore(v.nudges)
      snapCoins()
    },
  )

  // -- per-frame chrome (HUD band 90-99) -----------------------------------
  engine.addSystem({
    id: 'ui.hud',
    order: 94,
    fixed: (dt) => {
      const target = wallet.coins
      if (shownCoins !== target) {
        const delta = target - shownCoins
        const step = Math.max(1, Math.abs(delta) * 0.25)
        shownCoins += Math.sign(delta) * Math.min(step, Math.abs(delta))
        if (Math.abs(target - shownCoins) < 0.5) shownCoins = target
        paintPurse()
      }
      if (bumpFor > 0) {
        bumpFor -= dt
        if (bumpFor <= 0) delete purse.dataset['bump']
      }
      if (dodgeHold > 0) {
        dodgeHold -= dt
        if (dodgeHold <= 0) cam.dataset['dodge'] = '0'
      }
    },
  })

  snapCoins()

  // -- test surface (its own global; window.__harness stays architect-owned) -
  window.__ui = {
    ready: true,
    panel: () => panels.open,
    openPanel: (name) => panels.show(name),
    closePanel: () => panels.hide(),
    nudge: (id) => nudges.force(id),
    clearNudge: () => nudges.clear(),
    // The three transient pictures the reported screen-filling coin could have
    // been — a flying reward, a toast badge and the purse — forced on screen at
    // once so `ui-check` can size them. They are otherwise never in a captured
    // frame, which is exactly why the artifact was never reproducible.
    fly: () => {
      const at = { ...app.playerPos }
      for (let i = 0; i < 4; i++) {
        bus.emit('ui:fly', { icon: i % 2 === 0 ? 'coin' : 'bag', at, amount: 10 * (i + 1) })
      }
      bus.emit('ui:toast', { icon: 'coin', ms: 4000 })
      bus.emit('ui:toast', { icon: 'bag', ms: 4000 })
      bus.emit('ui:toast', { icon: 'star', ms: 4000 })
      purse.dataset['bump'] = '1'
    },
    tickets: () => tickets.demo(),
    clearTickets: () => tickets.clear(),
    ticketCount: () => tickets.count(),
    portrait: (on) => touchUx.forceRotateOverlay(on),
    isPortrait: () => touchUx.isPortrait(),
    dodge: () => cam.dataset['dodge'] === '1',
    coins: () => Math.round(shownCoins),
    buttons: () => measureTargets(hud),
    oversized: (fraction = 0.3) => measureOversized(hud, ctx.canvas, fraction),
    saveNow: () => save.flush('ui'),
    // The shelf signs the same contract the build drawer's `paletteLayout()`
    // does: `offscreen` must always be 0. Gated by tools/fix-hud-r2-verify.mjs.
    shopLayout: () => shopLayout(),
    signs: () => hud.querySelectorAll('.mt-sign').length,
    // Proof the shop is showing the real objects and not the flat fallback.
    thumbs: () => thumbCacheSize(),
  }
}

/**
 * ANY PICTURE IN THE HUD THAT COVERS MORE OF THE SCREEN THAN IT SHOULD.
 *
 * Exists because of a reported frame in which a single coin picture ~830 px
 * across covered the middle of the game while the purse, the camera cluster and
 * the left stack were all missing — an icon whose stylesheet had not landed, so
 * its viewBox-only `<svg>` inflated to the width of its container (see the long
 * note in `icons.ts`). `iconEl()` now sizes itself inline so that cannot happen;
 * this is the assertion that proves it stayed fixed, and `tools/ui-check.mjs`
 * fails the run on it.
 *
 * It deliberately measures PICTURES ONLY — icons, images, canvases, raw svg —
 * and not layout boxes. A panel body, a button rail or a scroll column is
 * supposed to be as wide as the screen; a coin is not. Measured against the
 * CANVAS bounding rect, never `window.innerWidth` — the hard rule applies here
 * too. The photo-mode postcard is the one picture that is meant to be huge.
 */
function measureOversized(
  hud: HTMLElement,
  canvas: HTMLCanvasElement,
  fraction: number,
): { label: string; w: number; h: number; frac: number }[] {
  const rect = canvas.getBoundingClientRect()
  const maxW = Math.max(1, rect.width * fraction)
  const maxH = Math.max(1, rect.height * fraction)
  const out: { label: string; w: number; h: number; frac: number }[] = []
  for (const node of hud.querySelectorAll<HTMLElement>('.mt-ico, img, canvas, svg')) {
    // The postcard IS the picture a kid is looking at; it is allowed to fill it.
    if (node.closest('.mt-postcard') !== null) continue
    const r = node.getBoundingClientRect()
    if (r.width <= maxW && r.height <= maxH) continue
    out.push({
      label: node.getAttribute('aria-label') ?? node.className ?? node.tagName,
      w: Math.round(r.width),
      h: Math.round(r.height),
      frac:
        Math.round(Math.max(r.width / rect.width, r.height / rect.height) * 100) / 100,
    })
  }
  return out
}

/**
 * Every interactive HUD target, measured in CSS px from the CANVAS rect. Drives
 * the touch-target gate in `tools/ui-check.mjs`: anything under 88 px, or any
 * two controls whose centres are closer than a finger, is a failure.
 */
function measureTargets(hud: HTMLElement): { label: string; w: number; h: number }[] {
  const out: { label: string; w: number; h: number }[] = []
  for (const node of hud.querySelectorAll('button, .mt-slot, .mt-swatch, .mt-card')) {
    const r = node.getBoundingClientRect()
    if (r.width === 0 && r.height === 0) continue
    out.push({
      label: node.getAttribute('aria-label') ?? node.className,
      w: Math.round(r.width),
      h: Math.round(r.height),
    })
  }
  return out
}

export interface UiTestApi {
  readonly ready: boolean
  panel(): string | null
  openPanel(name: string): boolean
  closePanel(): void
  nudge(id: string): boolean
  /** Force flying rewards + toasts on screen so their size can be gated. */
  fly(): void
  clearNudge(): void
  tickets(): void
  clearTickets(): void
  ticketCount(): number
  portrait(on: boolean | null): void
  isPortrait(): boolean
  dodge(): boolean
  coins(): number
  buttons(): { label: string; w: number; h: number }[]
  /** HUD widgets covering more than `fraction` of the canvas width. Should be []. */
  oversized(fraction?: number): { label: string; w: number; h: number; frac: number }[]
  saveNow(): boolean
  /** Proof that nothing on the shop shelf is unreachable. `offscreen` === 0. */
  shopLayout(): {
    aisle: string
    page: number
    pages: number
    perPage: number
    total: number
    rendered: number
    offscreen: number
  }
  /** How many signposts are standing in the world right now. */
  signs(): number
  /** How many shop pieces have been rendered as real 3D thumbnails. */
  thumbs(): number
}

declare global {
  interface Window {
    __ui?: UiTestApi
  }
}
